import type {
  AIModelChoices,
  AluminiumComposition,
  Annotation,
  EvaluatedSpecification,
  Evidence,
  EvidenceUploadMode,
  HDPEComposition,
  PETComposition,
} from 'api';
import { addError, logger } from 'monitor';
import { useEffect, useRef, useState } from 'react';

import { useManageEvidence } from '../../../evidence';
import { usePresentationPreference } from '../../../evidence/hooks/usePresentationPreference/usePresentationPreference';
import { revokeObjectUrl } from '../../../files';
import { formatEvidenceRejectionReason } from '../../../grade';
import { useNetworkStatus } from '../../../network-status';
import type { PreviewGalleryImage } from '../../types';
import { getPreviewImage } from '../../utils/getPreviewImage/getPreviewImage';
import { playAudio } from '../../utils/playAudio/playAudio';
import { vibrateDoublePulse } from '../../utils/vibrate/vibrate';
import { useNotificationPreferences } from '../useNotificationPreferences/useNotificationPreferences';
/**
 * @deprecated
 */ export const useManagePreviews = ({
  mode,
  onDeleteSuccess,
  parentId,
  rejectionSoundUrl,
}: {
  mode: EvidenceUploadMode;
  onDeleteSuccess?: () => void;
  parentId: string | number;
  rejectionSoundUrl?: string;
}) => {
  const { isOnline } = useNetworkStatus();
  const { vibrationEnabled, audioEnabled } = useNotificationPreferences();
  const { obfuscateAi } = usePresentationPreference();
  const [previewImages, setPreviewImages] = useState<PreviewGalleryImage[]>([]);
  const previewImagesRef = useRef<PreviewGalleryImage[]>([]);

  useEffect(() => {
    previewImagesRef.current = previewImages;
  }, [previewImages]);

  useEffect(() => {
    return () => {
      previewImagesRef.current.forEach((image) => {
        revokeObjectUrl(image.src);
      });
    };
  }, []);

  const { deleteEvidence } = useManageEvidence({
    mode,
    onSuccess: () => {
      onDeleteSuccess?.();
    },
    parentId,
    showNotification: false,
  });

  const addImage = async ({ photo }: { photo: File }) => {
    const previewImage = await getPreviewImage({
      disableDelete: true,
      feedbackMessages: [],
      file: photo,
      message: isOnline ? (obfuscateAi ? 'Processing photo...' : 'Analyzing photo...') : 'Stored offline',
      status: isOnline ? 'uploading' : 'offline',
    });
    setPreviewImages((prev) => [...prev, previewImage]);

    return previewImage;
  };

  const getFeedbackMessages = (
    accepted: boolean | undefined,
    warning: boolean | undefined,
    imageRejectionReasons: string[],
    _imageWarningReasons: string[]
  ) => {
    if (!accepted) {
      return imageRejectionReasons.map((reason) => formatEvidenceRejectionReason(reason, obfuscateAi));
    }
    if (warning) {
      return [];
    }
    return [];
  };

  const getImageStatus = (
    accepted: boolean | undefined,
    warning: boolean | undefined
  ): PreviewGalleryImage['status'] => {
    if (!accepted) {
      return 'warning';
    }
    if (warning) {
      return 'success';
    }
    return 'success';
  };

  const updateOnlineImage = async ({
    previewImage,
    accepted,
    warning,
    imageRejectionReasons,
    imageWarningReasons,
    url,
    aluminiumComposition,
    hdpeComposition,
    petComposition,
    targetWeightPercentage,
    aiModel,
    annotations,
    evaluatedSpecs,
  }: {
    previewImage: PreviewGalleryImage;
    accepted?: boolean;
    warning?: boolean;
    imageRejectionReasons: string[];
    imageWarningReasons: string[];
    url: string;
    aluminiumComposition?: AluminiumComposition;
    hdpeComposition?: HDPEComposition;
    petComposition?: PETComposition;
    targetWeightPercentage?: number;
    aiModel?: AIModelChoices;
    annotations?: Annotation[];
    evaluatedSpecs?: EvaluatedSpecification[];
  }) => {
    const originalSrc = previewImage.src;

    const feedbackMessages = getFeedbackMessages(accepted, warning, imageRejectionReasons, imageWarningReasons);
    const status = getImageStatus(accepted, warning);

    previewImage.status = status;
    previewImage.feedbackMessages = feedbackMessages;
    previewImage.message = accepted
      ? obfuscateAi
        ? 'Image processed successfully!'
        : 'Image successfully analysed!'
      : obfuscateAi
        ? 'Photo failed to be processed due to:'
        : 'Photo failed to be AI analysed due to:';
    previewImage.disableDelete = false;
    previewImage.src = url;
    previewImage.aluminiumComposition = aluminiumComposition;
    previewImage.hdpeComposition = hdpeComposition;
    previewImage.petComposition = petComposition;
    previewImage.targetWeightPercentage = targetWeightPercentage;
    previewImage.aiModel = aiModel;
    previewImage.annotations = annotations;
    previewImage.evaluatedSpecs = evaluatedSpecs;

    if (!accepted) {
      logger.info('Image rejected by AI analysis');

      if (!audioEnabled || !rejectionSoundUrl) {
        logger.info('Audio is disabled', { audioEnabled, rejectionSoundUrl });
      } else {
        logger.info('Playing rejection sound', { rejectionSoundUrl });
        await playAudio(rejectionSoundUrl);
      }

      if (!vibrationEnabled) {
        logger.info('Vibration is disabled');
      } else {
        logger.info('Vibrating device');
        vibrateDoublePulse();
      }
    }

    logger.info('Updated online image', {
      accepted,
      aiModel,
      aluminiumComposition,
      hdpeComposition,
      imageRejectionReasons,
      imageWarningReasons,
      previewImage,
      targetWeightPercentage,
      url,
      warning,
    });

    setPreviewImages((prev) => prev.map((i) => (i.src === originalSrc ? previewImage : i)));
    if (originalSrc !== url) {
      revokeObjectUrl(originalSrc);
    }
  };

  const updateOfflineImage = ({
    previewImage,
    id,
    status,
    message,
  }: {
    previewImage: PreviewGalleryImage;
    id: string;
    status?: PreviewGalleryImage['status'];
    message?: string;
  }) => {
    previewImage.id = id;
    previewImage.status = status || previewImage.status;
    previewImage.message = message || previewImage.message;
    previewImage.disableDelete = false;
    setPreviewImages((prev) => prev.map((i) => (i.src === previewImage.src ? previewImage : i)));
  };

  const handleDeletePreviewImage = ({
    image,
    evidence,
    removeEvidenceFile,
  }: {
    image: PreviewGalleryImage;
    evidence: Evidence[];
    removeEvidenceFile: (id: string) => void;
  }) => {
    const removePreviewImage = () => {
      setPreviewImages((prev) => prev.filter((previewImage) => previewImage.src !== image.src));
      revokeObjectUrl(image.src);
    };

    if (!isOnline && image.id) {
      removeEvidenceFile(image.id);
      removePreviewImage();
      return;
    }

    const evidenceItem = evidence?.find((e) => e.file === image.src);
    if (!evidenceItem) {
      const error = new Error('Could not find evidence file to delete');
      addError(error);
      throw error;
    }

    try {
      deleteEvidence({ data: { evidence_ids: [evidenceItem.id] } });
      removePreviewImage();
    } catch (error) {
      addError(error);
    }
  };

  const resetSelectedImages = () => {
    setPreviewImages((previousImages) => {
      previousImages.forEach((image) => {
        revokeObjectUrl(image.src);
      });

      return [];
    });
  };

  return {
    addImage,
    handleDeletePreviewImage,
    previewImages,
    resetSelectedImages,
    updateOfflineImage,
    updateOnlineImage,
  };
};
